import { useCallback, useContext, useEffect, useRef } from 'react'
import useFetch, { CachePolicies } from 'use-http'
import { isUndefined } from 'lodash'
import { FeaturesEnum } from '../../utils/features'
import type { MediaLibrarySearchResponse, MLSort } from '../interfaces'
import { UserContext } from '../providers'
import { PublicLinksInfoContext } from '../providers/PublicLinksInfoProvider'

interface Data<T> {
  success: boolean
  items: T[]
  error: unknown
}

interface UseMediaLibrarySearchProps<T> {
  path: string | null
  skip: number
  mediaCollectionRoute?: boolean
  collectionId?: true | string
  isDrawerSearch?: boolean
  limit: number
  sort: MLSort
  searchQuery: string
  semantic?: string
  semanticUrl?: string
  minScore?: number
  semanticSupported?: boolean
  suppressRequest?: boolean
  onData(data: Data<T>): void
  onLoading(value: boolean): void
}

function useMediaLibrarySearch<T = MediaLibrarySearchResponse>({
  path = '/',
  skip,
  mediaCollectionRoute,
  collectionId,
  isDrawerSearch,
  limit,
  sort,
  searchQuery,
  semantic,
  semanticUrl,
  semanticSupported = false,
  minScore,
  onData,
  onLoading,
  suppressRequest = false,
}: UseMediaLibrarySearchProps<T>): {
  loading: boolean
  refetch(options?: Partial<UseMediaLibrarySearchProps<T>>): Promise<void>
  abort(): void
} {
  const { isPublicLink, urlPrefix } = useContext(PublicLinksInfoContext)
  const { user } = useContext(UserContext)
  const skipToUse = useRef<Number>(0)

  const generateUrl = useCallback(
    (overrideParams: Partial<UseMediaLibrarySearchProps<T>> = {}): string => {
      skipToUse.current = isUndefined(overrideParams.skip)
        ? skip
        : overrideParams.skip
      const params: Record<string, string> = {
        skip: skipToUse.current.toString(),
        limit: (isUndefined(overrideParams.limit)
          ? limit
          : overrideParams.limit
        ).toString(),
        sort: isUndefined(overrideParams.sort) ? sort : overrideParams.sort,
        searchQuery: isUndefined(overrideParams.searchQuery)
          ? searchQuery
          : overrideParams.searchQuery,
      }

      const finalSemantic = isUndefined(overrideParams.semantic)
        ? semantic
        : overrideParams.semantic
      const finalSemanticUrl = isUndefined(overrideParams.semanticUrl)
        ? semanticUrl
        : overrideParams.semanticUrl
      const finalMinScore = isUndefined(overrideParams.minScore)
        ? minScore
        : overrideParams.minScore
      const finalPath = isUndefined(overrideParams.path)
        ? path
        : overrideParams.path

      if (finalSemanticUrl) {
        params.semanticUrl = finalSemanticUrl
      } else if (finalSemantic) {
        params.semantic = finalSemantic
      } else if (finalPath !== null) {
        params.path = finalPath
      }

      if (!isUndefined(finalMinScore)) {
        params.minScore = finalMinScore.toString()
      }

      const searchParams = new URLSearchParams(params)

      return `?${searchParams.toString()}`
    },
    [path, skip, limit, sort, searchQuery, semantic, semanticUrl, minScore],
  )

  const constructEndpoint = useCallback(
    (overrideParams: Partial<UseMediaLibrarySearchProps<T>> = {}): string => {
      let endpoint = ''

      const collectionIdParam =
        overrideParams.collectionId !== undefined
          ? overrideParams.collectionId
          : collectionId

      if (collectionIdParam === true) {
        endpoint = `${urlPrefix}api/v2/media-collections${generateUrl(
          overrideParams,
        )}`
      } else if (typeof collectionIdParam === 'string') {
        endpoint = `${urlPrefix}api/v2/media-collections/${collectionIdParam}/assets${generateUrl(
          overrideParams,
        )}`
      } else {
        endpoint = `${urlPrefix}api/v2/files${generateUrl(overrideParams)}`
      }

      return endpoint
    },
    [urlPrefix, collectionId, generateUrl],
  )

  const endpoint = constructEndpoint()

  const { loading, get, data, abort } = useFetch<Data<T>>({
    interceptors: {
      request: ({ options, path: requestPath }) => {
        if (
          requestPath?.startsWith(`${urlPrefix}api/v2/media-collections`) &&
          !isPublicLink &&
          !user?.pricingPlan.featuresList.includes(
            FeaturesEnum.ACL_AND_COLLECTIONS,
          )
        ) {
          const abortController = new AbortController()
          abortController.abort()

          return {
            ...options,
            signal: abortController.signal,
          }
        }
        return options
      },
    },
    cachePolicy: CachePolicies.NO_CACHE,
    data: { success: true, error: null, items: [] },
    onNewData: (currentData: Data<T>, newData: T[]) => {
      if (skipToUse.current === 0) {
        if (!Array.isArray(newData)) {
          return { success: false, error: newData, items: [] }
        }
        return { success: true, error: null, items: newData }
      }

      return {
        success: true,
        error: null,
        items: [...(currentData?.items || []), ...newData],
      }
    },
  })

  useEffect(() => {
    if (suppressRequest) {
      return
    }

    if (isPublicLink && isDrawerSearch) {
      return
    }
    // in the public-link UI, at the top-level 'All Media Collections' page, don't fire files/folder fetch requests
    if (
      isPublicLink &&
      mediaCollectionRoute &&
      typeof collectionId === 'undefined'
    ) {
      return
    }
    // in the public-link UI, at the top-level 'Home' page, don't fire /media-collection fetch request
    if (
      isPublicLink &&
      !mediaCollectionRoute &&
      typeof collectionId !== 'undefined'
    ) {
      return
    }

    if ((semantic || semanticUrl) && !semanticSupported && !isDrawerSearch) {
      onData({ success: true, error: null, items: [] })
      return
    }

    if (!isDrawerSearch && skip > 0) {
      return
    }
    void get(endpoint)
  }, [
    path,
    skip,
    limit,
    sort,
    searchQuery,
    collectionId,
    isDrawerSearch,
    mediaCollectionRoute,
    semantic,
    semanticUrl,
    semanticSupported,
    suppressRequest,
  ])

  useEffect(() => {
    onLoading(loading)
  }, [loading])

  useEffect(() => {
    onData(data ?? { success: false, error: null, items: [] })
  }, [data])

  const refetch = useCallback(
    (
      overrideParams: Partial<UseMediaLibrarySearchProps<T>> = {},
    ): Promise<any> => {
      if (suppressRequest) {
        return Promise.resolve()
      }
      const newEndpoint = constructEndpoint(overrideParams)
      return get(newEndpoint)
    },
    [get, constructEndpoint, suppressRequest],
  )

  return { refetch, loading, abort }
}

export default useMediaLibrarySearch
